import React, { useState } from 'react';
import {
  Box, Typography, TextField, Radio, Stack, FormControl, InputLabel, Select, MenuItem, InputAdornment, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import { createNewOrganization, acceptOrganizationInvitation } from '../../services/organizationService.js';
import { MENTAL_HEALTH_SPECIALITIES } from '../../constants/profileOptions.js';
import OnboardingLayout from './OnboardingLayout.js';

const TEAL = '#29C0BB';

const PanelBox = ({ selected, onSelect, children }) => (
  <Box
    onClick={onSelect}
    sx={{
      flex: 1,
      border: selected ? `2px solid ${TEAL}` : '1px solid #d7dde3',
      borderRadius: 3,
      p: 3,
      cursor: 'pointer',
      backgroundColor: '#fff',
      transition: 'border-color 0.15s',
    }}
  >
    {children}
  </Box>
);

const Step1Organization = ({ ctx, onContinue, onStepClick }) => {
  const { userId, initials, invitedMetadata, org, setOrg } = ctx;
  const { setAlert } = useAlert();
  const invited = Boolean(invitedMetadata?.team_id);

  // If the user already belongs to a team (resume, or invite processed
  // earlier), the join panel starts selected and shows the connected org.
  const [panel, setPanel] = useState(org || invited ? 'join' : 'create');
  const [orgName, setOrgName] = useState('');
  const [clinicType, setClinicType] = useState('');
  const [joinName, setJoinName] = useState(invitedMetadata?.teamName || '');
  const [joinResult, setJoinResult] = useState(null); // 'not_found' | 'invitation_required' | null
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleCreate = async () => {
    if (!orgName.trim() || !clinicType) {
      setAlert('Please enter an organization name and select a clinic type.', 'error');
      return false;
    }
    const { error, teamId } = await createNewOrganization(userId, orgName.trim(), []);
    if (error) {
      setAlert(error.message, 'error');
      return false;
    }
    // Clinic type mirrors into the user's Type/Speciality, the team name into
    // the profile's Organization display field; mode is fixed to Mental
    // Health for all new accounts (legacy column, no longer chosen).
    const { error: accountError } = await supabase
      .from('user_accounts')
      .update({ practicetype: clinicType, organisation: orgName.trim(), mode: 'Mental Health' })
      .eq('id', userId);
    if (accountError) console.error(accountError);
    setOrg({ id: teamId, name: orgName.trim(), role: 'Admin' });
    return true;
  };

  const handleInvitedJoin = async () => {
    const { data, error } = await acceptOrganizationInvitation(invitedMetadata.team_id);
    if (error || !data?.team_id) {
      setAlert('A valid invitation is required. Please ask your organization admin to send a new invitation.', 'error');
      return false;
    }
    await supabase.from('user_accounts')
      .update({ mode: 'Mental Health', organisation: data.team_name || '' })
      .eq('id', userId);
    setOrg({ id: data.team_id, name: data.team_name, role: data.role });
    return true;
  };

  const handleJoinBySearch = async () => {
    if (!joinName.trim()) {
      setAlert('Please enter the exact organization name.', 'error');
      return false;
    }
    const { data, error } = await supabase.rpc('join_team_by_exact_name', { p_name: joinName.trim() });
    if (error) {
      setAlert('Unable to search organizations. Please try again.', 'error');
      console.error(error);
      return false;
    }
    if (data?.status === 'SUCCESS' || data?.status === 'ALREADY_MEMBER') {
      await supabase.from('user_accounts')
        .update({ mode: 'Mental Health', organisation: data.team_name || '' })
        .eq('id', userId);
      setOrg({ id: data.team_id, name: data.team_name, role: data.role });
      setJoinResult(null);
      return true;
    }
    setJoinResult(data?.status === 'INVITATION_REQUIRED' ? 'invitation_required' : 'not_found');
    return false;
  };

  const handleContinue = async () => {
    setSubmitted(true);
    setSaving(true);
    try {
      let ok;
      if (org) {
        ok = true; // already connected (resume) — just move on
      } else if (panel === 'create') {
        ok = await handleCreate();
      } else if (invited) {
        ok = await handleInvitedJoin();
      } else {
        ok = await handleJoinBySearch();
      }
      if (ok) onContinue();
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout
      step={1}
      title="Create or join an organization"
      description="You'll use this organization to manage your practice on TikCare."
      avatarInitials={initials}
      onStepClick={onStepClick}
      onContinue={handleContinue}
      saving={saving}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        {/* Create panel */}
        <PanelBox selected={panel === 'create'} onSelect={() => !org && setPanel('create')}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Radio checked={panel === 'create'} size="small" disabled={Boolean(org)} sx={{ p: 0.5, color: TEAL, '&.Mui-checked': { color: TEAL } }} />
            <Typography sx={{ fontWeight: 700 }}>
              I'm starting my own practice / I'm the first one on my team to use TikCare
            </Typography>
          </Stack>
          <Box sx={{ mt: 3, opacity: panel === 'create' ? 1 : 0.6 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Organization name *</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter organization name"
              value={orgName}
              disabled={panel !== 'create' || Boolean(org)}
              onChange={(e) => setOrgName(e.target.value)}
              error={submitted && panel === 'create' && !orgName.trim()}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, mt: 2.5 }}>Clinic type *</Typography>
            <FormControl fullWidth size="small" error={submitted && panel === 'create' && !clinicType}>
              <InputLabel>Select clinic type</InputLabel>
              <Select
                value={clinicType}
                label="Select clinic type"
                disabled={panel !== 'create' || Boolean(org)}
                onChange={(e) => setClinicType(e.target.value)}
              >
                {MENTAL_HEALTH_SPECIALITIES.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </PanelBox>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: '#8a94a0' }}>OR</Typography>
        </Box>

        {/* Join panel */}
        <PanelBox selected={panel === 'join'} onSelect={() => setPanel('join')}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Radio checked={panel === 'join'} size="small" sx={{ p: 0.5, color: TEAL, '&.Mui-checked': { color: TEAL } }} />
            <Box>
              <Typography sx={{ fontWeight: 700 }}>I'm joining an existing clinic</Typography>
              <Typography variant="body2" sx={{ color: '#5b6770' }}>Search for your organization</Typography>
            </Box>
          </Stack>
          <Box sx={{ mt: 3, opacity: panel === 'join' ? 1 : 0.6 }}>
            {org ? (
              <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                Connected to <strong>{org.name}</strong> as {org.role}.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Organization name *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter the exact organization name"
                  value={joinName}
                  disabled={panel !== 'join' || invited}
                  onChange={(e) => { setJoinName(e.target.value); setJoinResult(null); }}
                  error={submitted && panel === 'join' && !joinName.trim()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: '#8a94a0' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {invited && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    You were invited to join <strong>{invitedMetadata.teamName}</strong>. Continue to accept.
                  </Alert>
                )}
                {joinResult === 'invitation_required' && (
                  <Alert severity="info">Please ask your organization admin to invite you. Knowing the organization name does not grant access.</Alert>
                )}
                {joinResult === 'not_found' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No organization found with that exact name.
                  </Alert>
                )}
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f6f8f9', borderRadius: 2, display: 'flex', gap: 1 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: TEAL, mt: '2px' }} />
                  <Typography variant="body2" sx={{ color: '#5b6770' }}>
                    New members need an email invitation from their organization admin.
                    Existing members can enter the exact organization name to reconnect.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </PanelBox>
      </Stack>

      {/* Invite fallback banner */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0fbfa', borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <MailOutlineIcon sx={{ color: TEAL }} />
        <Typography variant="body2">
          <strong>Can't find your organization?</strong>{' '}
          You can ask your organization admin to invite you through email.
        </Typography>
      </Box>
    </OnboardingLayout>
  );
};

export default Step1Organization;
